
import {
  defineComponent, ref, inject, computed, ComputedRef,
} from 'vue';
import { useI18n } from 'vue-i18n';
import PageHeader from '@/components/layout/PageHeader.vue';
import Tooltip from 'primevue/tooltip';
import SpmTableAPI from '@/components/table-rest-api/SpmTableAPI.vue';
import SpmDateDisplay from '@/components/table-rest-api/SpmDateDisplay.vue';
import SpmBadge from '@/components/table-rest-api/SpmBadge.vue';
import { NestApiType } from '@/composables/nestApi';
import type {
  SpmColumnConfig,
  ApiCallConfig,
  SpmApiFilter,
  ExportPayload,
} from '@/components/table-rest-api/types';
import { showToastError } from '@/helpers';

export default defineComponent({
  name: 'ManufacturersV2',
  components: {
    PageHeader,
    SpmTableAPI,
    SpmDateDisplay,
    SpmBadge,
  },

  directives: {
    tooltip: Tooltip,
  },

  setup() {
    const { t } = useI18n();

    const globalSourceFilters = inject<ComputedRef<SpmApiFilter[]>>('globalSourceFilters', computed(() => []));
    const persistentFilters = computed(() => [...globalSourceFilters.value]);

    // API call configuration
    const apiCall = ref<ApiCallConfig | null>({
      type_api: NestApiType.CLIENT_API,
      path: '/products-manufacturers',
      secure: true,
    });

    const columns: SpmColumnConfig[] = [
      {
        id: 'id',
        header: '',
        fields: ['id'],
        hidden: true,
      },
      {
        id: 'manufacturer_id',
        header: '',
        fields: [{
          name: 'manufacturer_id',
          label: t('myShopData.manufacturers.headers.id_manufacturer'),
          sortable: true,
          filterable: true,
          filterConfig: {
            type: 'textInput',
          },
        }],

        hidden: true,
      },
      {
        id: 'manufacturer_info',
        header: t('myShopData.manufacturers.headers.name'),
        fields: [{
          name: 'name',
          label: t('myShopData.manufacturers.headers.name'),
          sortable: true,
          filterable: true,
          filterConfig: {
            type: 'textInput',
          },
        }, {
          name: 'created_at',
          label: t('myShopData.manufacturers.headers.date_creation'),
          sortable: true,
          filterable: true,
          filterConfig: {
            type: 'dateRange',
          },
        }, {
          name: 'updated_at',
          label: t('myShopData.manufacturers.headers.date_modification'),
          sortable: true,
          filterable: true,
          filterConfig: {
            type: 'dateRange',
          },
        }],
      },
      {
        id: 'active',
        header: t('myShopData.manufacturers.headers.active'),
        fields: [{
          name: 'is_active',
          label: t('myShopData.manufacturers.headers.active'),
          sortable: true,
          filterable: true,
          filterConfig: {
            type: 'multiSelect',
            options: [
              { value: 1, label: t('active') },
              { value: 0, label: t('inactive') },
            ],
          },
        }],
      },
    ];

    // Helper function to clean strings
    const cleanStr = (value: string | number | null | undefined): string => {
      if (value === null || value === undefined || value === '') {
        return '';
      }
      return String(value).trim();
    };

    /**
     * Handle export action from SpmTableAPI
     */
    const handleExport = async (payload: ExportPayload) => {
      try {
        // Build export request payload - already simplified from SpmTableAPI
        const exportRequest: Record<string, unknown> = {
          entity_type: payload.exportConfig.entityType,
          select_all_mode: payload.selectAllMode,
          selected_ids: payload.selectedIds,
          total_count: payload.totalCount,
          ...payload.exportConfig.additionalParams,
        };

        // Add filters only if present (selectAllMode from selection, not "Export all" button)
        if (payload.filters) {
          exportRequest.filters = payload.filters;
        }

        console.log('Export request:', exportRequest);
        // TODO : Call export API
        // const response = await nestCall(
        //   'post',
        //   NestApiType.CLIENT_API,
        //   '/exports/manufacturers',
        //   exportRequest,
        //   '',
        //   undefined,
        //   true,
        // );

        // if (response && !response.error) {
        //   await showToastSuccess(t('myShopData.manufacturers.export.success'));
        // } else {
        //   await showToastError(t('myShopData.manufacturers.export.error'));
        // }
      } catch (error) {
        console.error('Export error:', error);
        await showToastError(t('myShopData.manufacturers.export.error'));
      }
    };

    return {
      t,
      apiCall,
      columns,
      persistentFilters,
      cleanStr,
      handleExport,
    };
  },
});
